import {ArrowLongRightIcon} from "@heroicons/react/24/outline";
import {
    toCentsFromMoney,
    toDecimalFromMoney,
    toMoneyFromCents,
    type Money,
} from "../../../../../lib/Money.ts";
import {signColorClass} from "../../../trialBalance/components/PeriodicStatementTable/tableStyles.ts";
import {MoneyLabel} from "../../../../general/MoneyLabel.tsx";
import {formatDate} from "../../../../../lib/utils.ts";
import type {PanelTotals} from "../types.ts";

interface PanelHeaderProps {
    bookingCount: number;
    periodStartDate: string;
    periodEndDate: string;
    totals: PanelTotals;
    openingBalance: Money | null;
}

function PeriodRange({startDate, endDate}: {startDate: string; endDate: string}) {
    const sameYear = startDate.slice(0, 4) === endDate.slice(0, 4);
    const start = sameYear
        ? formatDate(startDate, {day: "2-digit", month: "2-digit"})
        : formatDate(startDate);
    return (
        <span className="inline-flex items-center gap-1">
            {start}
            <ArrowLongRightIcon className="size-3.5 shrink-0 text-muted-foreground" aria-label="tot en met" />
            {formatDate(endDate)}
        </span>
    );
}

function toCreditPositive(debitPositive: Money): Money {
    return toMoneyFromCents(-debitPositive.signed_amount, debitPositive.currency);
}

function SignedAmount({creditPositive}: {creditPositive: Money}) {
    return (
        <span className={signColorClass(toCentsFromMoney(creditPositive))}>
            <MoneyLabel
                value={Math.abs(toDecimalFromMoney(creditPositive))}
                currency={creditPositive.currency}
                showCurrency={true}
            />
        </span>
    );
}

export function PanelHeader({
    bookingCount,
    periodStartDate,
    periodEndDate,
    totals,
    openingBalance,
}: PanelHeaderProps) {
    return (
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 pb-3">
            <span className="text-sm text-muted-foreground">
                {bookingCount} {bookingCount === 1 ? "boeking" : "boekingen"}
                {" · "}
                <PeriodRange startDate={periodStartDate} endDate={periodEndDate} />
            </span>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground">
                {openingBalance && (
                    <span>
                        <span className="text-muted-foreground">Beginbalans</span>{" "}
                        <span className="font-semibold">
                            <SignedAmount creditPositive={openingBalance} />
                        </span>
                    </span>
                )}
                <span>
                    <span className="text-muted-foreground">Debet</span>{" "}
                    <span className="font-semibold">
                        <MoneyLabel
                            value={toDecimalFromMoney(totals.debit)}
                            currency={totals.debit.currency}
                            showCurrency={true}
                        />
                    </span>
                </span>
                <span>
                    <span className="text-muted-foreground">Credit</span>{" "}
                    <span className="font-semibold">
                        <MoneyLabel
                            value={toDecimalFromMoney(totals.credit)}
                            currency={totals.credit.currency}
                            showCurrency={true}
                        />
                    </span>
                </span>
                <span>
                    <span className="text-muted-foreground">Saldo</span>{" "}
                    <span className="font-semibold">
                        <SignedAmount creditPositive={toCreditPositive(totals.saldo)} />
                    </span>
                </span>
            </div>
        </div>
    );
}
